import type { OHLCV } from '@shared/types/candle'

export type IndicatorCategory = 'trend' | 'momentum' | 'volatility' | 'volume' | 'professional'

export type ParameterType = 'number' | 'color' | 'boolean' | 'select'

export interface ParameterDef {
  key: string
  label: string
  type: ParameterType
  default: number | string | boolean
  min?: number
  max?: number
  step?: number
  options?: { value: string; label: string }[]
}

export interface IndicatorLineDef {
  key: string
  label: string
  color: string
  width: number
  style?: 'solid' | 'dashed' | 'dotted'
  /**
   * 'line' (default) — standard connected line series.
   * 'dots' — point markers only, no connecting line (used for Parabolic SAR).
   * 'area' — filled area series; fill colour comes from areaFillColor.
   * 'histogram' — vertical bars; per-bar colors supplied via IndicatorLineResult.colors[].
   *   When used as an overlay the series is placed on priceScaleId:'volume' with
   *   scaleMargins {top:0.8,bottom:0} so bars occupy the bottom 20% of the price pane.
   */
  renderStyle?: 'line' | 'dots' | 'area' | 'histogram'
  /** Fill colour for renderStyle:'area' series (semi-transparent recommended). */
  areaFillColor?: string
}

export interface IndicatorHistogramDef {
  key: string
  label: string
  colorPositive: string
  colorNegative: string
}

export interface IndicatorConfig {
  id: string
  name: string
  shortName: string
  category: IndicatorCategory
  overlay: boolean
  params: ParameterDef[]
  lines: IndicatorLineDef[]
  /** Histogram series definitions (e.g. MACD histogram). Only for non-overlay indicators. */
  histogramDefs?: IndicatorHistogramDef[]
  /**
   * Fixed price scale bounds for bounded oscillators (e.g. RSI: 0–100).
   * When set, the sub-pane price scale is locked to this range with no margin padding.
   * Leave unset for unbounded indicators like MACD, ATR, OBV.
   */
  priceRange?: { min: number; max: number }
  /**
   * Horizontal reference lines drawn in the sub-pane at these price values.
   * E.g. [70, 30] for RSI, [80, 20] for MFI, [0] for CMF zero line.
   * IndicatorPane renders them as dashed lines on the first line series.
   */
  priceLevels?: number[]
}

export interface IndicatorLineResult {
  key: string
  values: (number | null)[]
  /** Per-bar color overrides; parallel to values[]. Used by renderStyle:'histogram'. */
  colors?: (string | null)[]
}

export interface IndicatorBandResult {
  upper: (number | null)[]
  lower: (number | null)[]
  fillColor?: string
}

export interface IndicatorHistogramResult {
  values: (number | null)[]
  colors?: string[]
}

export interface IndicatorResult {
  lines: IndicatorLineResult[]
  bands?: IndicatorBandResult[]
  histograms?: IndicatorHistogramResult[]
}

export abstract class IndicatorBase {
  abstract readonly config: IndicatorConfig
  abstract calculate(
    candles: OHLCV[],
    params: Record<string, number | string | boolean>
  ): IndicatorResult
}
